import { z } from 'zod';

/**
 * Path of the public short-link landing page for a referral code. The `i`
 * segment is listed in `RESERVED_ROOT_SEGMENTS` (`@reflex/shared/routes`).
 */
export function referralPath(shortCode: string): string {
  return `/i/${encodeURIComponent(shortCode)}`;
}

/** Path of the signed-in page that shows a user's link and who joined through it. */
export const REFERRALS_PAGE_PATH = '/referrals';

/**
 * How long a code stashed by the landing page stays claimable, measured from
 * the moment it was written.
 *
 * The stash exists to cover one sign-in. sessionStorage outlives that by the
 * whole life of the tab, so without a deadline a code followed and abandoned
 * would still be sitting there to credit some unrelated account later. Half an
 * hour is far longer than a sign-in needs and far shorter than a working
 * session.
 *
 * It lives here rather than beside the browser code because the server's
 * attribution window is derived from it: the two have to move together, and a
 * comment across the server/web boundary is not a mechanism.
 */
export const PENDING_REFERRAL_TTL_MS = 30 * 60 * 1000;

/**
 * A user's personal referral link, as returned by the referrals API. The
 * field is `shortCode` rather than `code` to match the `short_code` analytics
 * property the events are keyed on.
 */
export const ReferralLinkSchema = z.object({
  shortCode: z.string(),
  /** Absolute short-link URL (`https://<app>/i/<code>`), no query string. */
  url: z.string(),
});
export type ReferralLink = z.infer<typeof ReferralLinkSchema>;

/** `GET /referrals/me` — whether the feature is on, the caller's link, and how many joined. */
export const ReferralStatusSchema = z.object({
  enabled: z.boolean(),
  link: ReferralLinkSchema.nullable(),
  /** Accounts credited to this link. Zero when there is no link yet. */
  signupCount: z.number().int().nonnegative(),
});
export type ReferralStatus = z.infer<typeof ReferralStatusSchema>;

/**
 * Where an organization stands with billing, as far as a referrer needs to
 * know: is this one paying yet?
 *
 * `none` is an org with no subscription window at all, which on this
 * deployment means unlimited access rather than a lapsed plan — worth keeping
 * distinct from `trial` so the page never reports a self-hosted org as being
 * on a countdown.
 */
export const ReferralOrgPlanSchema = z.enum(['paid', 'trial', 'none']);
export type ReferralOrgPlan = z.infer<typeof ReferralOrgPlanSchema>;

/** An organization a referred user created after following the link. */
export const ReferralSignupOrgSchema = z.object({
  id: z.string(),
  name: z.string(),
  plan: ReferralOrgPlanSchema,
});
export type ReferralSignupOrg = z.infer<typeof ReferralSignupOrgSchema>;

/**
 * One account credited to the caller's link.
 *
 * Deliberately no email address: the referrer shared a link, which anyone can
 * follow, so the people on this list have not agreed to hand the referrer
 * their contact details. A display name, a date and what they built is what
 * answers "did my link work".
 */
export const ReferralSignupSchema = z.object({
  id: z.string(),
  /** Epoch ms the signup was credited. */
  joinedAt: z.number(),
  /** Display name, or null when the account has none to show. */
  name: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  /** Organizations this person created after following the link. Often empty. */
  organizations: z.array(ReferralSignupOrgSchema),
});
export type ReferralSignup = z.infer<typeof ReferralSignupSchema>;

/** `GET /referrals/signups` — everyone who joined through the caller's link, newest first. */
export const ReferralSignupsSchema = z.object({
  signups: z.array(ReferralSignupSchema),
});
export type ReferralSignups = z.infer<typeof ReferralSignupsSchema>;

/** `POST /referrals/claim` request body: the code the visitor arrived on. */
export const ClaimReferralSchema = z.object({
  code: z.string().min(1).max(64),
});
export type ClaimReferral = z.infer<typeof ClaimReferralSchema>;

/** `POST /referrals/claim` response. */
export const ClaimReferralResultSchema = z.object({
  /** True only when this call is the one that credited the account. */
  attributed: z.boolean(),
});
export type ClaimReferralResult = z.infer<typeof ClaimReferralResultSchema>;

/**
 * `GET /referrals/resolve/:code` — public, so it carries nothing but the
 * validity bit. Everything the code stands for stays server side.
 */
export const ResolveReferralSchema = z.object({
  valid: z.boolean(),
});
export type ResolveReferral = z.infer<typeof ResolveReferralSchema>;
